import { useCallback, useEffect, useRef, useState } from 'react';
import { Close, Fullscreen, OpenInNew } from '@mui/icons-material';
import { Alert, AlertTitle, Drawer, IconButton } from '@mui/material';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import DrawerContext from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import { APP_DRAGGABLE_DRAWER_HEIGHT_VAR, MIN_DRAWER_HEIGHT } from './DraggableDrawer.constants';
import DraggableDrawerProps from './DraggableDrawer.props';
import { DrawerContentWrapper, DrawerHeader, DrawerHeaderContent, DrawerSizeBar } from './DraggableDrawer.styles';
import { DRAWER_EXTRACTION } from '../../../common/constants/features';
import useCheckFeatures from '../../FeatureEnabler/hooks';

// Global state to persist height across page changes
let globalHeight = MIN_DRAWER_HEIGHT;
let lastPagePath = '';

const DraggableDrawer = ({
  children,
  headerContent,
  contentWrapperSx,
  headerContentSx,
  ...rest
}: DraggableDrawerProps) => {
  const location = useLocation();
  const [drawerHeight, setDrawerHeight] = useState(globalHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [isPopped, setIsPopped] = useState(false);
  const popoutWindowRef = useRef<Window | null>(null);
  const popoutContainerRef = useRef<HTMLDivElement | null>(null);
  const initialMouseYRef = useRef<number>(0);
  const initialHeightRef = useRef<number>(0);
  const [, checkFeature] = useCheckFeatures();
  const isDrawerExtractionEnabled = checkFeature(DRAWER_EXTRACTION);

  const DRAWER_HEIGHT_MULTIPLIER = 10;

  // Check if page changed and reset height if needed
  useEffect(() => {
    if (lastPagePath !== location.pathname) {
      lastPagePath = location.pathname;
      globalHeight = MIN_DRAWER_HEIGHT;
      setDrawerHeight(MIN_DRAWER_HEIGHT);
    }
  }, [location.pathname]);

  // Update global height when state changes
  useEffect(() => {
    globalHeight = drawerHeight;
    // Expose current drawer height as a CSS variable so higher-level containers can respond
    try {
      const root = document.documentElement;
      root.style.setProperty(APP_DRAGGABLE_DRAWER_HEIGHT_VAR, `${Math.max(drawerHeight, MIN_DRAWER_HEIGHT)}px`);
    } catch {
      // no-op: document might be unavailable in some environments
    }
  }, [drawerHeight]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      initialMouseYRef.current = event.clientY;
      initialHeightRef.current = drawerHeight;
      setIsDragging(true);
    },
    [drawerHeight]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging) {
        event.preventDefault();
        const deltaY = initialMouseYRef.current - event.clientY;
        const newHeight = initialHeightRef.current + deltaY;

        setDrawerHeight(newHeight < MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT : newHeight);
      }
    },
    [isDragging, setDrawerHeight]
  );

  const handleFullScreen = useCallback(() => {
    setDrawerHeight(window.innerHeight);
  }, []);

  const handleMinimize = useCallback(() => {
    setDrawerHeight(MIN_DRAWER_HEIGHT);
  }, []);

  const handleAuxiliaryOpen = useCallback(() => {
    const doubleClickHeight =
      drawerHeight === MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT * DRAWER_HEIGHT_MULTIPLIER : MIN_DRAWER_HEIGHT;
    setDrawerHeight(doubleClickHeight);
  }, [setDrawerHeight, drawerHeight]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const doubleClickHeight =
        drawerHeight === MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT * DRAWER_HEIGHT_MULTIPLIER : MIN_DRAWER_HEIGHT;
      if (e.altKey && e.key === 'ArrowUp') {
        e.preventDefault();
        setDrawerHeight(prev => doubleClickHeight + prev);
      } else if (e.altKey && (e.key === 'ArrowDown' || e.key === 'c')) {
        e.preventDefault();
        setDrawerHeight(MIN_DRAWER_HEIGHT);
      } else if (e.altKey && e.key === 'o') {
        e.preventDefault();
        const availableHeight = window.innerHeight - 100;
        setDrawerHeight(availableHeight < MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT : availableHeight);
      }
    },
    [drawerHeight]
  );

  const handlePopout = useCallback(() => {
    const popoutWindow = window.open(
      '',
      'DrawerPopout',
      'width=800,height=600,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!popoutWindow) {
      return;
    }

    popoutWindowRef.current = popoutWindow;

    // Copy all stylesheets from parent window
    const stylesheets = Array.from(document.styleSheets);
    stylesheets.forEach(stylesheet => {
      try {
        if (stylesheet.href) {
          const link = popoutWindow.document.createElement('link');
          link.rel = 'stylesheet';
          link.href = stylesheet.href;
          popoutWindow.document.head.appendChild(link);
        } else if (stylesheet.cssRules) {
          const style = popoutWindow.document.createElement('style');
          Array.from(stylesheet.cssRules).forEach(rule => {
            style.appendChild(popoutWindow.document.createTextNode(rule.cssText));
          });
          popoutWindow.document.head.appendChild(style);
        }
      } catch (e) {
        console.warn('Could not copy stylesheet', e);
      }
    });

    // Create container for React content
    const container = popoutWindow.document.createElement('div');
    container.id = 'popout-root';
    popoutWindow.document.body.appendChild(container);
    popoutContainerRef.current = container;

    // Handle window close
    popoutWindow.addEventListener('beforeunload', () => {
      setIsPopped(false);
      popoutWindowRef.current = null;
      popoutContainerRef.current = null;
    });

    setIsPopped(true);
  }, []);

  const handlePopIn = useCallback(() => {
    if (popoutWindowRef.current) {
      popoutWindowRef.current.close();
    }
    setIsPopped(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Cleanup popout window on unmount
  useEffect(
    () => () => {
      if (popoutWindowRef.current) {
        popoutWindowRef.current.close();
      }
      // Reset the global CSS variable on unmount to avoid stale spacing on pages without the drawer
      try {
        document.documentElement.style.setProperty(APP_DRAGGABLE_DRAWER_HEIGHT_VAR, `${MIN_DRAWER_HEIGHT}px`);
      } catch {
        // ignore
      }
    },
    []
  );

  const drawerContent = (
    <>
      <DrawerSizeBar
        onMouseDown={handleMouseDown}
        onDoubleClick={handleAuxiliaryOpen}
      />

      {/* Create a scroll container that includes both header and content */}
      <div
        style={{
          flex: 1,
          overflow: drawerHeight === MIN_DRAWER_HEIGHT ? 'hidden' : 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {isPopped && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>
              Experimental Feature: This pop-out window is in early alpha and will contain visual inconsistencies. For
              best results, expand the drawer before popping it out.
            </AlertTitle>
          </Alert>
        )}
        <DrawerHeader>
          <DrawerHeaderContent
            sx={headerContentSx}
            onClick={() => drawerHeight === MIN_DRAWER_HEIGHT && handleAuxiliaryOpen()}>
            {headerContent}
          </DrawerHeaderContent>

          {/* Pop-out/Pop-in button */}
          {isDrawerExtractionEnabled && (
            <>
              {!isPopped ? (
                <IconButton onClick={handlePopout} title="Pop out to window">
                  <OpenInNew />
                </IconButton>
              ) : (
                <IconButton onClick={handlePopIn} title="Pop in">
                  <OpenInNew style={{ transform: 'rotate(180deg)' }} />
                </IconButton>
              )}
            </>
          )}

          {drawerHeight !== window.innerHeight && (
            <IconButton onClick={handleFullScreen}>
              <Fullscreen />
            </IconButton>
          )}
          {drawerHeight !== MIN_DRAWER_HEIGHT && (
            <IconButton onClick={handleMinimize}>
              <Close />
            </IconButton>
          )}
        </DrawerHeader>

        {/* Only render children when drawer is open */}
        {drawerHeight !== MIN_DRAWER_HEIGHT && (
          <DrawerContentWrapper sx={contentWrapperSx}>{children}</DrawerContentWrapper>
        )}
      </div>
    </>
  );

  return (
    <div onMouseMove={handleMouseMove as unknown as React.MouseEventHandler<HTMLDivElement>}>
      <DrawerContext.Provider value={{ drawerHeight, setDrawerHeight }}>
        {isPopped && popoutContainerRef.current ? (
          createPortal(
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>{drawerContent}</div>,
            popoutContainerRef.current
          )
        ) : (
          <Drawer
            open
            id="draggable-drawer"
            anchor="bottom"
            variant="persistent"
            sx={{
              '& .MuiDrawer-paper': {
                height: drawerHeight,
                overflowY: 'hidden',
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            {...rest}>
            {drawerContent}
          </Drawer>
        )}
      </DrawerContext.Provider>
    </div>
  );
};

export default DraggableDrawer;
